import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useIntl, defineMessages } from 'react-intl';
import {
  DropdownComponent,
  InputTextComponent,
  TextAreaComponent,
  FormikCheckbox,
} from '../../components/prime-formik';
import { STANDARD_ITEM_TYPE } from './constants';

const i18nMessages = defineMessages({
  itemType: {
    id: 'menu.item-bank-management.item-type',
    defaultMessage: 'Item Type',
  },
  itemAnswerType: {
    id: 'menu.item-bank-management.item-answer-type',
    defaultMessage: 'Item Answer Type',
  },
  itemCode: {
    id: 'menu.item-bank-management.item-code',
    defaultMessage: 'Item Code',
  },
  itemText: {
    id: 'menu.item-bank-management.item-text',
    defaultMessage: 'Item Text',
  },
  supportText: {
    id: 'menu.item-bank-management.support-text',
    defaultMessage: 'Support Text',
  },
  answerOption: {
    id: 'menu.item-bank-management.answer-option',
    defaultMessage: 'Answer Option',
  },
  correctAnswer: {
    id: 'menu.item-bank-management.correct-answer',
    defaultMessage: 'Correct Answer',
  },
});

const AddCognitiveItem = ({
  section, availableItemTypes, availableItemAnswerTypes, availableContexts,
}) => {
  const { formatMessage } = useIntl();

  const { values, errors, setFieldValue } = useFormikContext();

  const itemTypeOptions = availableItemTypes.map(itemType => ({
    value: itemType,
    label: itemType,
  }));

  const filteredItemAnswerTypes = availableItemAnswerTypes.filter(
    itemAnswerType => itemAnswerType.itemType === values.type && itemAnswerType.sectionCode === section,
  );

  const itemAnswerTypeOptions = filteredItemAnswerTypes.map(itemAnswerType => ({
    value: itemAnswerType.itemAnswerTypeId,
    label: itemAnswerType.itemAnswerType,
  }));

  const filteredContexts = availableContexts.filter(
    context => context.itemType === values.type && context.sectionCode === section,
  );

  const contextOptions = filteredContexts.map(context => ({
    value: context.code,
    label: context.code,
  }));

  const getNumberOfAnswers = () => {
    if (values.type && values.type === STANDARD_ITEM_TYPE && values.answerType) {
      const itemAnswerType = availableItemAnswerTypes.find(
        availableItemAnswerType => availableItemAnswerType.itemAnswerTypeId === values.answerType,
      );

      if (itemAnswerType.itemAnswerType === 'ITYPE_3VL_3') {
        return 3;
      }

      if (itemAnswerType.itemAnswerType === 'ITYPE_VALUE_LIST') {
        return 5;
      }
    }

    return 0;
  };

  return (
    <>
      <Field type="hidden" value={section} name="sectionCode" />
      <div className="items-controls">
        <div>
          <div className="input-label">{formatMessage(i18nMessages.itemType)}</div>
          <div>
            <Field id="type" name="type" component={DropdownComponent} options={itemTypeOptions} />
          </div>
        </div>
        <div>
          <div className="input-label">{formatMessage(i18nMessages.itemAnswerType)}</div>
          <div>
            <Field id="answerType" name="answerType" component={DropdownComponent} options={itemAnswerTypeOptions} />
          </div>
        </div>
      </div>

      <div className="items-controls">
        <div>
          <div className="input-label">{formatMessage(i18nMessages.itemCode)}</div>
          <div>
            <Field id="code" name="code" component={InputTextComponent} />
          </div>
        </div>
        <div>
          <div className="input-label">{formatMessage(i18nMessages.itemText)}</div>
          <div>
            <Field id="text" name="text" component={TextAreaComponent} />
          </div>
        </div>
      </div>

      <div className="context-option-controls">
        <div className="input-label">{formatMessage(i18nMessages.supportText)}</div>
        <div>
          <Field id="contextCode" name="contextCode" component={DropdownComponent} options={contextOptions} />
        </div>
      </div>

      <FieldArray
        name="answers"
        render={() => {
          const components = [];

          const answerErrors = () => {
            if (typeof errors.answers === 'string') {
              return <div>{errors.answers}</div>;
            }

            return null;
          };

          const numberOfAnswers = getNumberOfAnswers();

          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < numberOfAnswers; i++) {
            components.push(
              <div className="cognitive-item-answers margin-bottom">
                <span>
                  <strong className="margin-right">{`${formatMessage(i18nMessages.answerOption)} ${i + 1}`}</strong>
                  <span className="margin-right">
                    <Field id={`answers.${i}.text`} name={`answers.${i}.text`} component={InputTextComponent} />
                  </span>
                </span>
                <span>
                  <strong className="margin-right">{formatMessage(i18nMessages.correctAnswer)}</strong>
                  <span>
                    <Field
                      id={`answers.${i}.isCorrect`}
                      name={`answers.${i}.isCorrect`}
                      setFieldValue={setFieldValue}
                      component={FormikCheckbox}
                    />
                  </span>
                </span>
              </div>,
            );
          }

          return (
            <>
              {components}
              {answerErrors()}
            </>
          );
        }}
      />
    </>
  );
};

AddCognitiveItem.propTypes = {
  section: PropTypes.string.isRequired,
  availableItemTypes: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableItemAnswerTypes: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableContexts: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default AddCognitiveItem;
